/**
 * Имена файлов урока.
 *
 * Бэкенд отдаёт готовый адрес и, если повезёт, имя. Показывать «Материал 1»
 * нельзя: человек не понимает, что скачал, а без расширения файл ещё и
 * открывается не той программой. Поэтому имя берётся из данных, а при их
 * отсутствии — из последнего сегмента адреса.
 */

/** Поля, под которыми бэкенд может прислать имя файла. */
const NAME_KEYS = ['name', 'filename', 'file_name', 'title'];

export function fileNameOf(file: Record<string, unknown>, fallback: string): string {
  for (const key of NAME_KEYS) {
    const value = file[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  const url = typeof file.url === 'string' ? file.url : '';
  const fromUrl = decodeSegment(url.split('?')[0].split('/').filter(Boolean).pop() ?? '');
  if (fromUrl) return fromUrl;

  return fallback;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // Битая последовательность процентов: имя лучше показать как есть
    return segment;
  }
}

/**
 * Короткое имя для подписи.
 *
 * Длинные имена с датами и идентификаторами занимают несколько строк и
 * читаются хуже, чем обрезанные: середина заменяется многоточием, а расширение
 * сохраняется — по нему видно, что за файл.
 */
export function shortFileName(name: string, max = 28): string {
  if (name.length <= max) return name;

  const dot = name.lastIndexOf('.');
  const extension = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : '';
  const base = extension ? name.slice(0, dot) : name;

  const keep = max - extension.length - 1;
  if (keep <= 4) return `${name.slice(0, max - 1)}…`;

  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${base.slice(0, head)}…${base.slice(base.length - tail)}${extension}`;
}

/**
 * Итоговый файл задания, собранный ассистентом.
 *
 * Бэкенд называет их по схеме `Summary__module_1__task_3__<хеш>.pdf`: имя
 * служебное и человеку ничего не говорит. Из него собирается понятная подпись.
 */
const SUMMARY = /^summary__module_(\d+)__task_(\d+)(?:_v(\d+))?__/i;

/** Подпись файла на экране. */
export function lessonFileLabel(file: Record<string, unknown>, index: number): string {
  const name = fileNameOf(file, `Материал ${index + 1}`);
  const summary = SUMMARY.exec(name);
  if (!summary) return shortFileName(name);

  const variant = summary[3] ? `, вариант ${summary[3]}` : '';
  return `Итог — модуль ${summary[1]}, задание ${summary[2]}${variant}`;
}

/** Имя, под которым файл сохраняется на устройство: с расширением и без хеша. */
export function lessonFileName(file: Record<string, unknown>, index: number): string {
  const name = fileNameOf(file, `Материал ${index + 1}`);
  const summary = SUMMARY.exec(name);
  if (!summary) return name;

  const dot = name.lastIndexOf('.');
  const extension = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : '';
  const variant = summary[3] ? ` вариант ${summary[3]}` : '';
  return `Итог модуль ${summary[1]} задание ${summary[2]}${variant}${extension}`;
}
